// src/components/settings/RoleManagement.js
import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Paper,
  useTheme,
  useMediaQuery,
  Checkbox,
  Alert,
  Snackbar,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import {
  Add,
  Edit,
  Delete,
  Shield,
  AdminPanelSettings,
  AccountBalance,
  Handshake,
  Person,
  ContactMail,
  Business,
  Engineering,
  Receipt,
  ReceiptLong,
  CalendarMonth,
  Settings,
  Chat,
  Sell,
  ShoppingCart,
  LocalShipping,
  Description,
  Article,
  Task,
} from "@mui/icons-material";
import { useCollection } from "../../hooks/useCollection";
import { useFirestore } from "../../hooks/useFirestore";
import LoadingSpinner from "../ui/LoadingSpinner";
import EmptyState from "../ui/EmptyState";
import ConfirmDialog from "../ui/ConfirmDialog";

// IDs de módulos que coinciden con las rutas reales
const MODULES = [
  { id: "contacts", label: "Contactos", icon: <ContactMail /> },
  { id: "clients", label: "Clientes", icon: <Person /> },
  { id: "suppliers", label: "Proveedores", icon: <LocalShipping /> },
  { id: "commercials", label: "Comerciales", icon: <Handshake /> },
  { id: "companies", label: "Empresas", icon: <Business /> },
  { id: "projects", label: "Proyectos", icon: <Engineering /> },
  { id: "proposals", label: "Propuestas", icon: <Description /> },
  { id: "contracts", label: "Contratos", icon: <Article /> },
  { id: "sales", label: "Ventas (Facturas)", icon: <Receipt /> },
  { id: "purchases", label: "Compras (Facturas)", icon: <ReceiptLong /> },
  { id: "calendar", label: "Calendario", icon: <CalendarMonth /> },
  { id: "tasks", label: "Tareas", icon: <Task /> },
  { id: "settings", label: "Configuración", icon: <Settings /> },
  { id: "chat", label: "Chat", icon: <Chat /> },
  { id: "accounting", label: "Contabilidad", icon: <AccountBalance /> },
];

const RoleManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: roles, loading, refresh } = useCollection("roles");
  const firestore = useFirestore("roles");
  const [localRoles, setLocalRoles] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    modules: {},
  });

  React.useEffect(() => {
    if (roles) {
      console.log(
        "📋 Roles cargados:",
        roles.map((r) => ({
          id: r.id,
          name: r.name,
          modules: Object.keys(r.modules || {}),
        })),
      );
      setLocalRoles(roles);
    }
  }, [roles]);

  const handleOpenDialog = (role = null) => {
    if (role) {
      console.log(
        "✏️ Editando rol:",
        role.name,
        "| ID:",
        role.id,
        "| Módulos:",
        Object.keys(role.modules || {}),
      );
      setEditingRole(role);
      setFormData({
        name: role.name || "",
        description: role.description || "",
        modules: role.modules || {},
      });
    } else {
      console.log("➕ Nuevo rol");
      setEditingRole(null);
      setFormData({
        name: "",
        description: "",
        modules: {},
      });
    }
    setError("");
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingRole(null);
    setError("");
    setSaving(false);
  };

  // En la función handleModuleToggle de RoleManagement.js:

  const handleModuleToggle = (moduleId) => {
    setFormData((prev) => {
      const newModules = { ...prev.modules };
      if (newModules[moduleId]) {
        delete newModules[moduleId];
      } else {
        // Para propuestas, añadir viewAll por defecto
        newModules[moduleId] = {
          view: true,
          create: true,
          edit: true,
          delete: true,
          ...(moduleId === "proposals" ? { viewAll: true } : {}), // ← AÑADIR
        };
      }
      return { ...prev, modules: newModules };
    });
  };
  
  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError("El nombre del rol es obligatorio");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const roleData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        modules: formData.modules,
      };

      console.log("💾 Guardando rol:", {
        editing: editingRole?.id,
        name: roleData.name,
        modules: Object.keys(roleData.modules),
      });

      if (editingRole?.id) {
        await firestore.update(editingRole.id, roleData);
        console.log("✅ Rol actualizado");
        setSnackbar({
          open: true,
          message: `Rol "${roleData.name}" actualizado`,
          severity: "success",
        });
      } else {
        const result = await firestore.add(roleData);
        console.log("✅ Rol creado con ID:", result?.id);
        setSnackbar({
          open: true,
          message: `Rol "${roleData.name}" creado`,
          severity: "success",
        });
      }

      handleCloseDialog();
      if (refresh) await refresh();
    } catch (err) {
      console.error("❌ Error:", err);
      setError(err.message || "Error al guardar");
      setSnackbar({
        open: true,
        message: "Error: " + err.message,
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;

    try {
      console.log("🗑️ Eliminando:", deleteTarget.id, deleteTarget.name);
      await firestore.remove(deleteTarget.id);
      console.log("✅ Eliminado");

      setLocalRoles((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      setDeleteTarget(null);

      setSnackbar({
        open: true,
        message: `Rol "${deleteTarget.name}" eliminado`,
        severity: "success",
      });
      if (refresh) await refresh();
    } catch (err) {
      console.error("❌ Error:", err);
      setSnackbar({
        open: true,
        message: "Error: " + err.message,
        severity: "error",
      });
    }
  };

  const getRoleIcon = (roleName) => {
    const name = roleName?.toLowerCase() || "";
    if (name.includes("admin")) return <AdminPanelSettings />;
    if (name.includes("ceo") || name.includes("direct"))
      return <AdminPanelSettings />;
    if (name.includes("cont") || name.includes("finan"))
      return <AccountBalance />;
    if (name.includes("comercial") || name.includes("ventas"))
      return <Handshake />;
    if (name.includes("compra") || name.includes("proveedor"))
      return <LocalShipping />;
    return <Shield />;
  };

  const getModuleCount = (role) => Object.keys(role.modules || {}).length;

  const getModuleIcon = (moduleId) => {
    const module = MODULES.find((m) => m.id === moduleId);
    return module?.icon || <Shield />;
  };

  if (loading || !localRoles) return <LoadingSpinner />;

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 2, sm: 3 },
          borderRadius: { xs: "16px", sm: "20px" },
          border: "1px solid",
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.12)"
              : "#e2e8f0",
          background: theme.palette.background.paper,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          gap={1.5}
        >
          <Box>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
            >
              Roles del Sistema
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
            >
              Define los roles y sus permisos de acceso a los módulos
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={`Total: ${localRoles?.length || 0}`}
              sx={{
                height: 44,
                borderRadius: "12px",
                fontWeight: 700,
                fontSize: { xs: "0.75rem", sm: "0.85rem" },
                background: theme.palette.background.paper,
                border: "1px solid",
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
              }}
            />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
                height: 44,
                boxShadow: "none",
                fontSize: { xs: "0.8rem", sm: "0.85rem" },
                "&:hover": {
                  boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
                  filter: "brightness(1.05)",
                },
              }}
            >
              Nuevo Rol
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {localRoles.length === 0 ? (
        <EmptyState
          title="Sin roles configurados"
          description="Crea tu primer rol para comenzar a gestionar los permisos del sistema"
          actionLabel="Crear Primer Rol"
          onAction={() => handleOpenDialog()}
        />
      ) : (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {localRoles.map((role) => (
            <Grid item xs={12} sm={6} md={4} key={role.id}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: "16px",
                  border: "1px solid",
                  borderColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.12)"
                      : "#e2e8f0",
                  background: theme.palette.background.paper,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    boxShadow: `0 4px 12px ${theme.palette.primary.main}20`,
                  },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "12px",
                          background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.secondary.main}20)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: theme.palette.primary.main,
                        }}
                      >
                        {getRoleIcon(role.name)}
                      </Box>
                      <Box>
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
                        >
                          {role.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                        >
                          {getModuleCount(role)} módulos
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDialog(role);
                        }}
                        sx={{ color: theme.palette.primary.main }}
                      >
                        <Edit sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(role);
                        }}
                        sx={{ color: theme.palette.error.main }}
                      >
                        <Delete sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  {role.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2, fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
                    >
                      {role.description}
                    </Typography>
                  )}

                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {Object.keys(role.modules || {}).map((moduleId) => {
                      const module = MODULES.find((m) => m.id === moduleId);
                      return (
                        <Chip
                          key={moduleId}
                          icon={getModuleIcon(moduleId)}
                          label={module?.label || moduleId}
                          size="small"
                          sx={{
                            background: `${theme.palette.primary.main}15`,
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          }}
                        />
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Diálogo */}
      <Dialog
        open={showDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "24px" } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: { xs: "1.1rem", sm: "1.3rem" },
            pb: 1,
          }}
        >
          {editingRole ? `Editar: ${editingRole.name}` : "Nuevo Rol"}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>
              {error}
            </Alert>
          )}

          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Nombre del rol *"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              required
              placeholder="Ej: Administrador, Editor..."
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
            <TextField
              fullWidth
              size="small"
              label="Descripción"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              multiline
              rows={2}
              placeholder="Describe qué puede hacer este rol..."
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />

            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{ mt: 2, mb: 1 }}
            >
              Módulos disponibles
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Selecciona los módulos. IDs: sales = Ventas, purchases = Compras
            </Typography>

            {MODULES.map((module) => (
              <Paper
                key={module.id}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: formData.modules[module.id]
                    ? theme.palette.primary.main
                    : theme.palette.divider,
                  background: formData.modules[module.id]
                    ? `${theme.palette.primary.main}08`
                    : theme.palette.background.paper,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": { borderColor: theme.palette.primary.main },
                }}
                onClick={() => handleModuleToggle(module.id)}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Checkbox
                    checked={!!formData.modules[module.id]}
                    onChange={() => handleModuleToggle(module.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Box sx={{ color: theme.palette.primary.main }}>
                    {module.icon}
                  </Box>
                  <Box>
                    <Typography fontWeight={600} sx={{ fontSize: "0.875rem" }}>
                      {module.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {module.id}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{ borderRadius: "12px", textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.name.trim() || saving}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
            }}
          >
            {saving
              ? "Guardando..."
              : editingRole
                ? "Actualizar Rol"
                : "Crear Rol"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Eliminar Rol"
        message={`¿Eliminar el rol "${deleteTarget?.name}"?`}
        type="delete"
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: "12px", width: "100%" }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default RoleManagement;
